import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Skeleton, 
    Chip, 
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Switch,
    Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { RestaurantService } from '../../../api/services/restaurantService';
import CreateMenuItemModal from './CreateMenuItemModal';
import EditMenuItemModal from './EditMenuItemModal';
import ReplayIcon from '@mui/icons-material/Replay';
import { formatCurrency } from '../../../utils/currency';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardResetButtonSx } from '../../../components/common/tableStyles';

export default function MenuItemsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [updatingStatusIds, setUpdatingStatusIds] = useState({});
    
    // Pagination & Filter State
    const [page, setPage] = useState(0); // MUI uses 0-index
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [categoryId, setCategoryId] = useState('All');
    const [isAvailable, setIsAvailable] = useState('All');

    // Debounce search input (500ms)
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(0); // Reset page on new search
        }, 500);
        return () => clearTimeout(handler);
    }, [searchInput]);

    // Fetch Reference Categories for Filter Dropdown
    useEffect(() => {
        const fetchCategoriesLookup = async () => {
            try {
                // Fetch a large block of categories for the filter dropdown
                const response = await RestaurantService.getMenuCategories({ page: 1, noPagination: true });
                const dataArray = response?.data?.data || response?.data || response?.categories || [];
                setCategories(Array.isArray(dataArray) ? dataArray : []);
            } catch (error) {
                console.error('Failed to fetch categories lookup:', error);
            }
        };
        fetchCategoriesLookup();
    }, []);

    // Fetch Menu Items
    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // Backend expects 1-indexed pages
                limit: limit
            };
            
            if (debouncedSearch) params.search = debouncedSearch;
            if (categoryId !== 'All') params.categoryId = categoryId;
            if (isAvailable !== 'All') params.isAvailable = isAvailable;

            const response = await RestaurantService.getMenuItems(params);
            
            const dataArray = response?.data?.data || response?.data || response?.items || [];
            setItems(Array.isArray(dataArray) ? dataArray : []);
            setTotalRecords(extractTotalCount(response, dataArray));
        } catch (error) {
            console.error('Failed to fetch menu items:', error);
            // Error toast is handled globally by axios interceptor
        } finally {
            setLoading(false);
        }
    };

    // Refetch when filters or pagination change
    useEffect(() => {
        fetchItems();
    }, [page, limit, debouncedSearch, categoryId, isAvailable]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEditClick = (item) => {
        setSelectedItem(item);
        setEditModalOpen(true);
    };

    const handleStatusToggle = async (item, newStatus) => {
        setUpdatingStatusIds(prev => ({ ...prev, [item.id]: true }));
        try {
            await RestaurantService.changeMenuItemStatus(item.id, newStatus);
            // On Success: Keep localized layout synced and show toast
            fetchItems(); // Silently updates the list without breaking layout
            import('../../../utils/toast').then(module => {
                module.toast.success('Menu item visibility status updated successfully');
            });
        } catch (error) {
            console.error('Failed to change status:', error);
            // The item will naturally snap back to its previous state on UI because we only optimistically updated the loader, but didn't modify local array yet.
        } finally {
            setUpdatingStatusIds(prev => ({ ...prev, [item.id]: false }));
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setCreateModalOpen(true)}
                >
                    Create New Item
                </Button>
            </Box>

            {/* Filter Toolbar Layout */}
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search by name or description..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select 
                            value={categoryId} 
                            displayEmpty
                            onChange={(e) => {
                                setCategoryId(e.target.value);
                                setPage(0);
                            }}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Categories</MenuItem>
                            {categories.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select 
                            value={isAvailable} 
                            displayEmpty
                            onChange={(e) => {
                                setIsAvailable(e.target.value);
                                setPage(0);
                            }}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="true">Available</MenuItem>
                            <MenuItem value="false">Unavailable / Out-of-Stock</MenuItem>
                        </Select>
                    </FormControl>
                    {(searchInput !== '' || categoryId !== 'All' || isAvailable !== 'All') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            onClick={() => { setSearchInput(''); setCategoryId('All'); setIsAvailable('All'); setPage(0); }}
                            sx={standardResetButtonSx}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Menu Items Grid Data Table */}
            <TableContainer component={Paper}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell>Item Name & SKU</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Base Price</TableCell>
                            <TableCell align="center">Kitchen Status</TableCell>
                            <TableCell align="center">Public Visibility</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(limit)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Skeleton variant="rounded" width={40} height={40} />
                                            <Box sx={{ flex: 1 }}>
                                                <Skeleton width="80%" />
                                                <Skeleton width="40%" height={16} />
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell align="center"><Skeleton width={80} sx={{ display: 'inline-block' }} /></TableCell>
                                    <TableCell align="center"><Skeleton width={60} sx={{ display: 'inline-block' }} /></TableCell>
                                    <TableCell align="right"><Skeleton width={32} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No menu items found matching the current filters.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={item.image} 
                                                variant="rounded" 
                                                sx={{ 
                                                    width: 40, 
                                                    height: 40, 
                                                    border: '1px solid rgba(0,0,0,0.08)',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    bgcolor: '#f5f5f5'
                                                }}
                                            >
                                                {item.name ? item.name.charAt(0).toUpperCase() : 'M'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {item.name}
                                                </Typography>
                                                {(item.sku || item.id) && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        SKU: {item.sku || item.id.substring(0, 8)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={item.category?.name || item.categoryLabel || 'Uncategorized'} 
                                            size="small" 
                                            variant="outlined" 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                                            {formatCurrency(item.price || item.basePrice)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip 
                                            label={item.isAvailable ? 'Available' : 'Unavailable'} 
                                            size="small" 
                                            color={item.isAvailable ? 'success' : 'error'} 
                                            variant={item.isAvailable ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.isActive ? 'Published' : 'Draft'}
                                            </Typography>
                                            {updatingStatusIds[item.id] ? (
                                                <CircularProgress size={20} color="success" sx={{ ml: 1, mr: 1 }} />
                                            ) : (
                                                <Switch 
                                                    checked={!!item.isActive} 
                                                    onChange={(e) => handleStatusToggle(item, e.target.checked)}
                                                    size="small"
                                                    color="success"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(item)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                
                {/* MUI Standard Pagination Controls Footer */}
                <TablePagination
                    component="div"
                    count={items.length === 0 ? 0 : totalRecords}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <CreateMenuItemModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onItemCreated={fetchItems}
            />

            <EditMenuItemModal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedItem(null);
                }}
                item={selectedItem}
                onItemUpdated={fetchItems}
            />
        </Box>
    );
}
